import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TicketReport } from "@/features/tickets/TicketReport";
import type { TicketWithRefs } from "@/features/tickets/types";

export const Route = createFileRoute("/_authenticated/reports/ticket-department-breakdown")({
  component: () => (
    <TicketReport
      id="R-51"
      title="Department-wise Ticket Breakdown"
      description="Ticket counts grouped by asset department."
      enabledFilters={["branch", "priority", "status", "dateRange"]}
      filename="ticket-department-breakdown"
      buildRows={(t) => aggregate(t).map((r) => ({
        "Department": r.name, "Total": r.total, "Open": r.open, "Resolved": r.resolved, "Breached": r.breached,
      }))}
      render={(tickets) => {
        const rows = aggregate(tickets);
        return (
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Department</TableHead><TableHead>Total</TableHead>
                <TableHead>Open</TableHead><TableHead>Resolved</TableHead><TableHead>Breached</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.total}</TableCell>
                    <TableCell>{r.open}</TableCell>
                    <TableCell>{r.resolved}</TableCell>
                    <TableCell>{r.breached}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">No data.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        );
      }}
    />
  ),
});

const OPEN = new Set<string>([
  "Open", "Assigned", "Reassigned", "In Progress",
  "Awaiting Spare", "Awaiting Vendor", "Reopened",
]);

function aggregate(tickets: TicketWithRefs[]) {
  const map = new Map<string, { name: string; total: number; open: number; resolved: number; breached: number }>();
  for (const t of tickets) {
    const name = t.asset_department_name ?? "—";
    const row = map.get(name) ?? { name, total: 0, open: 0, resolved: 0, breached: 0 };
    row.total += 1;
    if (OPEN.has(t.status)) row.open += 1;
    if (t.status === "Resolved" || t.status === "Closed") row.resolved += 1;
    if (t.sla_status === "Response Breached" || t.sla_status === "Resolution Breached") row.breached += 1;
    map.set(name, row);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}
